import React, { useEffect, useState } from "react";
import { Plus, Trash2, Package, Search, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";
import CsvImport from "@/components/CsvImport";

function ItemModal({ open, onClose, onSaved, initial }) {
  const empty = {
    name: "", sku: "", hsn: "", unit: "pcs", kind: "product",
    sale_price: 0, purchase_price: 0, tax_rate: 18,
    opening_stock: 0, low_stock_threshold: 0, description: "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (initial) {
      setF({ ...empty, ...initial });
    } else {
      setF(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);
  if (!open) return null;
  const isEdit = Boolean(initial?.id);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        // Only send editable fields (exclude derived like stock_qty, low_stock, id)
        const payload = {
          name: f.name, sku: f.sku, hsn: f.hsn, unit: f.unit, kind: f.kind,
          sale_price: parseFloat(f.sale_price) || 0,
          purchase_price: parseFloat(f.purchase_price) || 0,
          tax_rate: parseFloat(f.tax_rate) || 0,
          opening_stock: parseFloat(f.opening_stock) || 0,
          low_stock_threshold: parseFloat(f.low_stock_threshold) || 0,
          description: f.description,
        };
        await api.put(`/items/${initial.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/items", f);
        toast.success("Item added");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="item-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">{isEdit ? `Edit ${f.name || "Item"}` : "New Item"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Kind</label>
            <select className="input-clean" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="product">Product</option><option value="service">Service</option>
            </select></div>
          <div><label className="label-field">Name *</label>
            <input data-testid="item-name" className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
          <div><label className="label-field">SKU / Code</label>
            <input className="input-clean font-mono-data" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })}/></div>
          <div><label className="label-field">HSN / SAC</label>
            <input data-testid="item-hsn" className="input-clean font-mono-data" value={f.hsn} onChange={(e) => setF({ ...f, hsn: e.target.value })}/></div>
          <div><label className="label-field">Unit</label>
            <input className="input-clean" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}/></div>
          <div><label className="label-field">Tax Rate %</label>
            <input type="number" step="0.01" className="input-clean font-mono-data" value={f.tax_rate} onChange={(e) => setF({ ...f, tax_rate: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Sale Price</label>
            <input data-testid="item-sale-price" type="number" step="0.01" className="input-clean font-mono-data" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Purchase Price</label>
            <input type="number" step="0.01" className="input-clean font-mono-data" value={f.purchase_price} onChange={(e) => setF({ ...f, purchase_price: parseFloat(e.target.value) || 0 })}/></div>
          {f.kind === "product" && (
            <>
              <div><label className="label-field">Opening Stock</label>
                <input type="number" step="0.01" className="input-clean font-mono-data" value={f.opening_stock} onChange={(e) => setF({ ...f, opening_stock: parseFloat(e.target.value) || 0 })}/></div>
              <div><label className="label-field">Low Stock Threshold</label>
                <input type="number" step="0.01" className="input-clean font-mono-data" value={f.low_stock_threshold} onChange={(e) => setF({ ...f, low_stock_threshold: parseFloat(e.target.value) || 0 })}/></div>
            </>
          )}
          <div className="md:col-span-2"><label className="label-field">Description</label>
            <textarea className="input-clean" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="item-save-btn" onClick={save} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Items() {
  const { active } = useCompany();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/items${lowOnly ? "?low_stock=true" : ""}`);
      setRows(data.items || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, lowOnly]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="items-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Inventory</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Items</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport
            endpoint="/items/import"
            testid="items-csv"
            sampleName="items_template"
            sampleHeaders={["name","sku","hsn","unit","kind","sale_price","purchase_price","tax_rate","opening_stock","low_stock_threshold","description"]}
            onImported={load}
          />
          <button onClick={() => setOpen(true)} data-testid="new-item-btn" className="btn-primary w-full sm:w-auto justify-center">
            <Plus size={14}/> New Item
          </button>
        </div>
      </div>

      <div className="card-surface p-4">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={16} className="text-[#94A3B8]"/>
            <input className="input-clean" placeholder="Search items…" value={q} onChange={(e) => setQ(e.target.value)}/>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#64748B]">
            <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} data-testid="filter-low-stock"/>
            Only low stock
          </label>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B]">No items yet.</p>
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Name</th><th>SKU</th><th>HSN</th><th>Unit</th>
                <th className="text-right">Sale Price</th>
                <th className="text-right">Stock</th><th>Tax %</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`item-row-${it.id}`}>
                  <td className="font-medium">
                    {it.name}
                    {it.low_stock && (
                      <span className="chip chip-danger ml-2 text-[10px]">
                        <AlertTriangle size={10}/> Low
                      </span>
                    )}
                  </td>
                  <td className="font-mono-data text-xs">{it.sku || "-"}</td>
                  <td className="font-mono-data text-xs">{it.hsn || "-"}</td>
                  <td>{it.unit}</td>
                  <td className="text-right font-mono-data">{fmtMoney(it.sale_price, active?.base_currency)}</td>
                  <td className="text-right font-mono-data">{it.kind === "service" ? "—" : it.stock_qty}</td>
                  <td className="font-mono-data">{it.tax_rate}%</td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`edit-item-${it.id}`}
                        onClick={() => { setEditing(it); setOpen(true); }}
                        className="text-[#1E3A8A] hover:text-[#0F172A]"
                        title="Edit"
                      ><Pencil size={14}/></button>
                      <button
                        data-testid={`delete-item-${it.id}`}
                        onClick={() => remove(it.id)}
                        className="text-[#DC2626]"
                        title="Delete"
                      ><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <ItemModal
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={load}
        initial={editing}
      />
    </div>
  );
}
